import type { WorkloadMetricsEntry, WorkloadMetricsInterval } from '@/lib/urm-api';

/** Simple GET is the cost baseline every other API is measured against. */
const BASELINE = { resourceType: 'General', action: 'get', apiLabel: 'resources' } as const;

export interface ApiCostRatioSummary {
  key: string;
  label: string;
  resourceType: string;
  action: string;
  apiLabel: string;
  /** p10, mean, and p90 of the per-interval (tested mean ÷ baseline mean).
   * p10/p90 instead of raw min/max so one outlier interval does not set them. */
  minRatio: number | null;
  avgRatio: number | null;
  maxRatio: number | null;
  /** Intervals where both this API and the baseline had requests. */
  comparedIntervals: number;
  windowMeanMs: number | null;
  baselinePresent: boolean;
}

function validCount(value: number): number {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

/** Linear-interpolated percentile over the sorted values. */
function percentile(values: number[], fraction: number): number {
  const sorted = [...values].sort((left, right) => left - right);
  const position = Math.min(1, Math.max(0, fraction)) * (sorted.length - 1);
  const lower = sorted[Math.floor(position)];
  const upper = sorted[Math.ceil(position)];
  return lower + (position - Math.floor(position)) * (upper - lower);
}

function entryKey(entry: WorkloadMetricsEntry): string {
  return JSON.stringify([entry.resource_type, entry.action.toLowerCase(), entry.api_label]);
}

function isBaseline(entry: WorkloadMetricsEntry): boolean {
  return entry.resource_type === BASELINE.resourceType
    && entry.action.toLowerCase() === BASELINE.action
    && entry.api_label === BASELINE.apiLabel;
}

/** Mean latency in ms for a set of entries, or null when there are no requests. */
function meanMs(entries: WorkloadMetricsEntry[]): number | null {
  let requests = 0;
  let processingNs = 0;
  for (const entry of entries) {
    requests += validCount(entry.request_count);
    processingNs += validCount(entry.total_processing_time_ns);
  }
  return requests === 0 ? null : processingNs / requests / 1_000_000;
}

interface MutableGroup {
  sample: WorkloadMetricsEntry;
  processingNs: number;
  requests: number;
  ratios: number[];
}

function summarize(key: string, group: MutableGroup, baselinePresent: boolean): ApiCostRatioSummary {
  const { sample, ratios } = group;
  const comparedIntervals = ratios.length;
  const avgRatio = comparedIntervals === 0
    ? null
    : ratios.reduce((sum, ratio) => sum + ratio, 0) / comparedIntervals;
  return {
    key,
    label: [sample.resource_type, sample.action.toLowerCase(), sample.api_label].join(' · '),
    resourceType: sample.resource_type || 'Unknown',
    action: sample.action.toLowerCase() || 'Unknown',
    apiLabel: sample.api_label || 'Unknown',
    minRatio: comparedIntervals === 0 ? null : percentile(ratios, 0.1),
    avgRatio,
    maxRatio: comparedIntervals === 0 ? null : percentile(ratios, 0.9),
    comparedIntervals,
    windowMeanMs: group.requests === 0 ? null : group.processingNs / group.requests / 1_000_000,
    baselinePresent,
  };
}

/** Per-API relative cost: at each interval, tested mean latency ÷ baseline
 * (General/get/resources) mean latency, then min/avg/max across the window.
 * Intervals missing either side are skipped so the ratio stays well-defined. */
export function buildApiCostRatioSummaries(
  intervals: WorkloadMetricsInterval[],
): ApiCostRatioSummary[] {
  const groups = new Map<string, MutableGroup>();
  let baselinePresent = false;

  for (const interval of intervals) {
    const baselineMean = meanMs(interval.entries.filter(isBaseline));
    if (baselineMean !== null) baselinePresent = true;

    const perKey = new Map<string, WorkloadMetricsEntry[]>();
    for (const entry of interval.entries) {
      const key = entryKey(entry);
      (perKey.get(key) ?? perKey.set(key, []).get(key)!).push(entry);
    }

    for (const [key, entries] of perKey) {
      const group = groups.get(key) ?? { sample: entries[0], processingNs: 0, requests: 0, ratios: [] };
      for (const entry of entries) {
        group.processingNs += validCount(entry.total_processing_time_ns);
        group.requests += validCount(entry.request_count);
      }
      const testedMean = meanMs(entries);
      if (testedMean !== null && baselineMean !== null && baselineMean > 0) {
        group.ratios.push(testedMean / baselineMean);
      }
      groups.set(key, group);
    }
  }

  return Array.from(groups, ([key, group]) => summarize(key, group, baselinePresent))
    .sort((left, right) => (right.avgRatio ?? -1) - (left.avgRatio ?? -1)
      || left.label.localeCompare(right.label));
}

/** Collapses the selected rows into one synthetic API per interval, then runs
 * the same ratio calculation. */
export function buildCombinedApiCostRatioSummary(
  intervals: WorkloadMetricsInterval[],
  selectedKeys: ReadonlySet<string>,
): ApiCostRatioSummary | null {
  if (selectedKeys.size === 0) return null;

  const combinedIntervals = intervals.map((interval) => {
    const selected = interval.entries.filter((entry) => selectedKeys.has(entryKey(entry)));
    const baselineEntries = interval.entries.filter(isBaseline);
    const entries: WorkloadMetricsEntry[] = baselineEntries.slice();
    if (selected.length > 0) {
      entries.push({
        username: '__combined__',
        resource_type: '__combined__',
        action: '__combined__',
        api_label: '__combined__',
        request_count: selected.reduce((sum, entry) => sum + validCount(entry.request_count), 0),
        total_processing_time_ns: selected.reduce(
          (sum, entry) => sum + validCount(entry.total_processing_time_ns),
          0,
        ),
      });
    }
    return { ...interval, entries };
  });

  const summary = buildApiCostRatioSummaries(combinedIntervals)
    .find((candidate) => candidate.apiLabel === '__combined__');
  if (!summary) return null;
  const rowCount = selectedKeys.size;
  return {
    ...summary,
    key: '__combined_selection__',
    label: `Combined selection (${rowCount} ${rowCount === 1 ? 'row' : 'rows'})`,
    resourceType: 'Combined selection',
    action: '',
    apiLabel: '',
  };
}
